import React, { useState, useEffect, useMemo, useRef } from "react";
import "../../assets/css/Tasks/TaskCanvasView.css";

/**
 * Robust date parser supporting YYYY-MM-DD, ISO timestamps, and Date objects
 */
function parseDate(dateStr) {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;
  if (typeof dateStr === "string") {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      if (dateStr.includes("T") || dateStr.includes(" ")) {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? new Date(year, month, day) : d;
      }
      return new Date(year, month, day);
    }
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format date string into long display (e.g. October 28, 2026)
 */
function formatLongDate(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return "No due date";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format date string into short display (e.g. Oct 28)
 */
function formatShortDate(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Determine if task is completed
 */
function isTaskCompleted(task) {
  if (!task) return false;
  return Boolean(
    task.status === "Complete" ||
    task.status === "Completed" ||
    task.completed_at ||
    task.completion_date
  );
}

/**
 * Determine if task is overdue
 */
function isTaskOverdue(task) {
  if (!task || !task.due_date) return false;
  if (isTaskCompleted(task)) return false;
  const due = parseDate(task.due_date);
  if (!due) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  return due < startOfToday;
}

/**
 * Effective status business rules:
 * 1. Completed tasks remain Complete(d) regardless of due date.
 * 2. Incomplete tasks with due_date < today are Overdue.
 * 3. Incomplete tasks due today or future are Pending.
 */
function getEffectiveStatus(task) {
  if (isTaskCompleted(task)) return "Complete";
  if (isTaskOverdue(task)) return "Overdue";
  return "Pending";
}

/**
 * Categorize task for Due Date Board:
 * - Overdue: incomplete with due < startOfToday
 * - Today: incomplete with due on today
 * - Upcoming: incomplete with due > endOfToday or no due date
 * - Completed: completed tasks
 */
function getDueDateCategory(task) {
  if (isTaskCompleted(task)) return "Completed";
  if (!task.due_date) return "Upcoming";
  const due = parseDate(task.due_date);
  if (!due) return "Upcoming";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if (due < startOfToday) return "Overdue";
  if (due >= startOfToday && due <= endOfToday) return "Today";
  return "Upcoming";
}

/**
 * Extract associated client / entity name from task
 */
function getTaskClient(task) {
  if (!task) return "";
  return (
    task.client ||
    task.client_name ||
    task.organization_name ||
    task.organization?.name ||
    task.lead_title ||
    task.deal_title ||
    task.contact_name ||
    ""
  );
}

export default function TaskCanvasView({
  tasks = [],
  loading = false,
  onTaskClick,
  onCompleteTask,
  onReopenTask,
  onDirectCompleteTask,
  onDirectReopenTask,
  onDeleteTask,
  canDelete = false,
}) {
  const [localTasks, setLocalTasks] = useState(tasks);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dueDateFilter, setDueDateFilter] = useState("ALL");
  const [groupBy, setGroupBy] = useState("dueDate");
  const [sortBy, setSortBy] = useState("dueDate");
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [activeDropdownMenuId, setActiveDropdownMenuId] = useState(null);

  // Drag-and-Drop state
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverColumnTitle, setDragOverColumnTitle] = useState(null);

  const searchInputRef = useRef(null);
  const inFlightUpdatesRef = useRef(new Set());
  const wasDraggingRef = useRef(false);
  const touchStateRef = useRef(null);
  const dragResetTimeoutRef = useRef(null);

  // Synchronize local tasks state when tasks prop updates
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Clean up drag reset timeout
  useEffect(() => {
    return () => {
      if (dragResetTimeoutRef.current) {
        clearTimeout(dragResetTimeoutRef.current);
      }
    };
  }, []);

  // Dynamic formatted today string for filter dropdown label
  const todayLabel = useMemo(() => {
    return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleWindowClick = () => {
      setActiveDropdownMenuId(null);
    };
    window.addEventListener("click", handleWindowClick);
    return () => window.removeEventListener("click", handleWindowClick);
  }, []);

  // Keyboard accessibility: Escape to close modals/menus, '/' to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedTaskForModal(null);
        setActiveDropdownMenuId(null);
      }
      if (
        e.key === "/" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Filter & Sort Pipeline
  const filteredAndSortedTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    const filtered = localTasks.filter((task) => {
      const eff = getEffectiveStatus(task);
      const ddCat = getDueDateCategory(task);

      // Status Filter
      if (statusFilter !== "ALL") {
        if (statusFilter === "Complete" || statusFilter === "Completed") {
          if (eff !== "Complete") return false;
        } else if (eff !== statusFilter) {
          return false;
        }
      }

      // Due Date Filter
      if (dueDateFilter === "Today" && ddCat !== "Today") return false;
      if (dueDateFilter === "Upcoming" && ddCat !== "Upcoming") return false;
      if (dueDateFilter === "Overdue" && ddCat !== "Overdue") return false;

      // Search Query across title, description, and client
      if (q) {
        const titleMatch = (task.title || "").toLowerCase().includes(q);
        const descMatch = (task.description || "").toLowerCase().includes(q);
        const clientMatch = getTaskClient(task).toLowerCase().includes(q);
        if (!titleMatch && !descMatch && !clientMatch) return false;
      }

      return true;
    });

    // Sorting (Due Date or Task Name)
    return [...filtered].sort((a, b) => {
      if (sortBy === "dueDate") {
        const aDate = a.due_date ? parseDate(a.due_date)?.getTime() || Infinity : Infinity;
        const bDate = b.due_date ? parseDate(b.due_date)?.getTime() || Infinity : Infinity;
        return aDate - bDate;
      }
      if (sortBy === "title") {
        return (a.title || "").localeCompare(b.title || "");
      }
      return 0;
    });
  }, [localTasks, searchQuery, statusFilter, dueDateFilter, sortBy]);

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    statusFilter !== "ALL" ||
    dueDateFilter !== "ALL";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setDueDateFilter("ALL");
  };

  const handleToggleComplete = (task) => {
    const isComp = isTaskCompleted(task);
    if (isComp) {
      if (onReopenTask) {
        onReopenTask(task);
      }
    } else {
      if (onCompleteTask) {
        onCompleteTask(task);
      }
    }
  };

  const toggleDropdownMenu = (id, e) => {
    e.stopPropagation();
    setActiveDropdownMenuId((prev) => (prev === id ? null : id));
  };

  const handleCardClick = (e, task) => {
    if (wasDraggingRef.current) return;
    if (e.target.closest("[data-action]")) return;
    setSelectedTaskForModal(task);
  };

  const handleCardKeyDown = (e, task) => {
    if (e.key === "Enter") {
      if (e.target.closest("[data-action]")) return;
      setSelectedTaskForModal(task);
    }
  };

  // Drag-and-Drop Task Status Update Executor
  // Exclusively triggers the completion / follow-up confirmation workflow
  const executeTaskDrop = (task, targetColumnTitle) => {
    if (!task) return;
    if (targetColumnTitle !== "Completed") return;
    if (isTaskCompleted(task)) return;

    if (inFlightUpdatesRef.current.has(task.id)) return;
    inFlightUpdatesRef.current.add(task.id);
    setTimeout(() => {
      inFlightUpdatesRef.current.delete(task.id);
    }, 800);

    // Trigger existing follow-up / completion dialog without premature state changes
    if (onCompleteTask) {
      onCompleteTask(task);
    }
  };

  // HTML5 Drag and Drop Handlers (Restricted to completing pending tasks)
  const handleCardDragStart = (e, task) => {
    // Only pending tasks can be dragged to Completed
    if (isTaskCompleted(task)) {
      e.preventDefault();
      return;
    }
    // Avoid starting drag on interactive elements
    if (e.target.closest("input, button, select, a, [data-action], .card-actions-dropdown")) {
      e.preventDefault();
      return;
    }
    wasDraggingRef.current = true;
    if (e.dataTransfer) {
      e.dataTransfer.setData("text/plain", String(task.id));
      e.dataTransfer.effectAllowed = "move";
    }
    setDraggedTaskId(task.id);
    setDraggedTask(task);
  };

  const handleCardDragEnd = () => {
    setDraggedTaskId(null);
    setDraggedTask(null);
    setDragOverColumnTitle(null);
    if (dragResetTimeoutRef.current) {
      clearTimeout(dragResetTimeoutRef.current);
    }
    dragResetTimeoutRef.current = setTimeout(() => {
      wasDraggingRef.current = false;
    }, 150);
  };

  const handleColumnDragOver = (e, columnTitle) => {
    e.preventDefault();
    // Only Completed column is a valid drop destination
    if (columnTitle !== "Completed") {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "none";
      }
      return;
    }
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "move";
    }
    if (dragOverColumnTitle !== "Completed") {
      setDragOverColumnTitle("Completed");
    }
  };

  const handleColumnDragLeave = (e, columnTitle) => {
    if (columnTitle !== "Completed") return;
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumnTitle(null);
    }
  };

  const handleColumnDrop = (e, columnTitle) => {
    e.preventDefault();
    setDragOverColumnTitle(null);
    if (columnTitle !== "Completed") {
      handleCardDragEnd();
      return;
    }
    const taskIdStr = e.dataTransfer ? e.dataTransfer.getData("text/plain") : null;
    const taskToDrop =
      draggedTask ||
      localTasks.find((t) => String(t.id) === String(taskIdStr));
    if (taskToDrop) {
      executeTaskDrop(taskToDrop, columnTitle);
    }
    handleCardDragEnd();
  };

  // Touch Support (for mobile/tablet devices)
  const handleCardTouchStart = (e, task) => {
    if (isTaskCompleted(task)) return;
    if (e.target.closest("input, button, select, a, [data-action], .card-actions-dropdown")) {
      return;
    }
    const touch = e.touches[0];
    touchStateRef.current = {
      task,
      startX: touch.clientX,
      startY: touch.clientY,
      isDragging: false,
    };
  };

  const handleCardTouchMove = (e, task) => {
    if (!touchStateRef.current || touchStateRef.current.task.id !== task.id) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStateRef.current.startX;
    const dy = touch.clientY - touchStateRef.current.startY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 10) {
      if (!touchStateRef.current.isDragging) {
        touchStateRef.current.isDragging = true;
        wasDraggingRef.current = true;
        setDraggedTaskId(task.id);
        setDraggedTask(task);
      }

      const elem = document.elementFromPoint(touch.clientX, touch.clientY);
      const colElem = elem?.closest(".canvas-column");
      const colTitle = colElem?.getAttribute("data-column-title");
      if (colTitle === "Completed") {
        setDragOverColumnTitle("Completed");
      } else {
        setDragOverColumnTitle(null);
      }
    }
  };

  const handleCardTouchEnd = () => {
    if (touchStateRef.current?.isDragging) {
      const task = touchStateRef.current.task;
      if (dragOverColumnTitle === "Completed" && task) {
        executeTaskDrop(task, "Completed");
      }
    }
    touchStateRef.current = null;
    handleCardDragEnd();
  };

  // Render individual task card
  const renderCard = (task) => {
    const eff = getEffectiveStatus(task);
    const isCompleted = eff === "Complete";
    const isOverdue = eff === "Overdue";
    const dueCat = getDueDateCategory(task);
    const isToday = dueCat === "Today";
    const client = getTaskClient(task);
    const isDragging = draggedTaskId === task.id;

    const statusBadgeClass =
      eff === "Complete"
        ? "badge-status-completed"
        : eff === "Overdue"
        ? "badge-status-overdue"
        : "badge-status-pending";

    let dueClass = "card-due-indicator";
    let dueLabel = task.due_date ? formatShortDate(task.due_date) : "No due date";
    if (isOverdue) {
      dueClass += " overdue-due";
      dueLabel = `Overdue • ${formatShortDate(task.due_date)}`;
    } else if (isToday) {
      dueClass += " today-due";
      dueLabel = "Due Today";
    }

    return (
      <div
        key={task.id}
        className={`task-canvas-card${isCompleted ? " is-completed" : ""}${
          isOverdue ? " is-overdue" : ""
        }${isDragging ? " is-dragging" : ""}`}
        data-id={task.id}
        tabIndex={0}
        role="button"
        draggable={!isCompleted}
        onDragStart={(e) => handleCardDragStart(e, task)}
        onDragEnd={handleCardDragEnd}
        onTouchStart={(e) => handleCardTouchStart(e, task)}
        onTouchMove={(e) => handleCardTouchMove(e, task)}
        onTouchEnd={handleCardTouchEnd}
        aria-label={`Task: ${task.title}`}
        onClick={(e) => handleCardClick(e, task)}
        onKeyDown={(e) => handleCardKeyDown(e, task)}
      >
        {/* Card Top Row */}
        <div className="card-top-row">
          <div className="card-left-meta">
            <input
              type="checkbox"
              className="card-checkbox"
              data-action="toggle-complete"
              data-id={task.id}
              checked={isCompleted}
              title={isCompleted ? "Mark as Incomplete" : "Mark as Complete"}
              aria-label={`Toggle completion for ${task.title}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleComplete(task);
              }}
              onChange={() => {}}
            />
            <span className={`badge ${statusBadgeClass}`}>{eff}</span>
          </div>
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="card-menu-btn"
              data-action="toggle-menu"
              data-id={task.id}
              title="Task actions"
              aria-label="Task options"
              onClick={(e) => toggleDropdownMenu(task.id, e)}
            >
              •••
            </button>
            {activeDropdownMenuId === task.id && (
              <div
                className="card-actions-dropdown show"
                id={`dropdown-${task.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="dropdown-item"
                  data-action="toggle-complete-menu"
                  data-id={task.id}
                  onClick={() => {
                    setActiveDropdownMenuId(null);
                    handleToggleComplete(task);
                  }}
                >
                  {isCompleted ? "↺ Mark Incomplete" : "✓ Mark Complete"}
                </button>
                {canDelete && onDeleteTask && (
                  <button
                    type="button"
                    className="dropdown-item danger-item"
                    data-action="delete-task"
                    data-id={task.id}
                    onClick={() => {
                      setActiveDropdownMenuId(null);
                      onDeleteTask(task);
                    }}
                  >
                    ✕ Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div className="card-body-section">
          <div className="card-task-title">{task.title}</div>
          {task.description && <p className="card-task-desc">{task.description}</p>}
          {client && (
            <div className="card-client-tag">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
              <span>{client}</span>
            </div>
          )}
        </div>

        {/* Card Footer */}
        <div className="card-bottom-row">
          <span className={dueClass}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>{dueLabel}</span>
          </span>
        </div>
      </div>
    );
  };

  // Render board column
  const renderColumn = (title, dotColor, items, emptyMessage) => {
    const isColumnDragOver = dragOverColumnTitle === title;

    return (
      <div
        className={`canvas-column${isColumnDragOver ? " is-drag-over" : ""}`}
        key={title}
        data-column-title={title}
        onDragOver={(e) => handleColumnDragOver(e, title)}
        onDragEnter={(e) => handleColumnDragOver(e, title)}
        onDragLeave={(e) => handleColumnDragLeave(e, title)}
        onDrop={(e) => handleColumnDrop(e, title)}
      >
        <div className="canvas-column-header">
          <div className="col-title-side">
            <span className="col-dot" style={{ background: dotColor }} />
            <span className="col-title">{title}</span>
          </div>
          <span className="col-counter-pill">{items.length}</span>
        </div>
        <div className="canvas-column-cards">
          {items.length === 0 && !isColumnDragOver ? (
            <div className="column-empty-state">{emptyMessage}</div>
          ) : (
            items.map(renderCard)
          )}
          {isColumnDragOver && draggedTask && (
            <div className="column-drop-placeholder">
              <span>Drop here to review follow-up and complete</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="task-canvas-clean-container">
      {/* Search and Filter Toolbar */}
      <div className="filter-toolbar">
          <div className="filter-controls-group">
            {/* Search */}
            <div className="search-input-wrapper">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                id="searchInput"
                className="search-input"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <select
              id="statusFilter"
              className="filter-select"
              aria-label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">Status: All</option>
              <option value="Pending">Pending</option>
              <option value="Complete">Complete</option>
              <option value="Overdue">Overdue</option>
            </select>

            {/* Priority Filter completely removed */}

            {/* Due Date Filter */}
            <select
              id="dueDateFilter"
              className="filter-select"
              aria-label="Filter by Due Date"
              value={dueDateFilter}
              onChange={(e) => setDueDateFilter(e.target.value)}
            >
              <option value="ALL">Due Date: All Dates</option>
              <option value="Today">Today ({todayLabel})</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Overdue">Overdue</option>
            </select>

            {/* Group By Selector (Priority removed) */}
            <select
              id="groupBySelect"
              className="filter-select"
              aria-label="Group tasks by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="dueDate">Group by: Due Date</option>
              <option value="status">Group by: Status</option>
              <option value="none">Group by: None</option>
            </select>

            {/* Sort Control (Priority removed) */}
            <select
              id="sortBySelect"
              className="filter-select"
              aria-label="Sort tasks by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="dueDate">Sort: Due Date</option>
              <option value="title">Sort: Task Name</option>
            </select>

            {/* Clear Filters Action */}
            <button
              type="button"
              id="clearFiltersBtn"
              className={`btn-clear-filters${hasActiveFilters ? " visible" : ""}`}
              title="Reset all active filters"
              onClick={clearFilters}
            >
              ✕ Clear Filters
            </button>
          </div>

          <div className="filter-meta-right">
            <span className="task-count-indicator" id="taskCountLabel">
              {filteredAndSortedTasks.length}{" "}
              {filteredAndSortedTasks.length === 1 ? "task" : "tasks"}
              {hasActiveFilters ? " (filtered)" : ""}
            </span>
          </div>
        </div>

        {/* D. Canvas Workspace */}
        <main className="canvas-workspace" id="canvasWorkspace">
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
              <p style={{ fontSize: "14px", fontWeight: "500" }}>Loading tasks...</p>
            </div>
          ) : groupBy === "none" ? (
            filteredAndSortedTasks.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-muted)" }}>
                <p style={{ fontSize: "15px", fontWeight: "600", color: "var(--text-main)" }}>
                  No tasks match the selected criteria
                </p>
                <p style={{ fontSize: "13px", marginTop: "4px" }}>
                  Try adjusting your search query or filters.
                </p>
              </div>
            ) : (
              <div className="canvas-flat-grid">
                {filteredAndSortedTasks.map(renderCard)}
              </div>
            )
          ) : groupBy === "status" ? (
            <div className="canvas-board-layout">
              {renderColumn(
                "Pending",
                "var(--primary)",
                filteredAndSortedTasks.filter((t) => getEffectiveStatus(t) === "Pending"),
                "No pending tasks"
              )}
              {renderColumn(
                "Overdue",
                "var(--status-overdue-dot)",
                filteredAndSortedTasks.filter((t) => getEffectiveStatus(t) === "Overdue"),
                "No overdue tasks"
              )}
              {renderColumn(
                "Completed",
                "var(--status-completed-dot)",
                filteredAndSortedTasks.filter((t) => getEffectiveStatus(t) === "Complete"),
                "No completed tasks"
              )}
            </div>
          ) : (
            // Default: Due Date
            <div className="canvas-board-layout">
              {renderColumn(
                "Overdue",
                "var(--status-overdue-dot)",
                filteredAndSortedTasks.filter((t) => getDueDateCategory(t) === "Overdue"),
                "No overdue tasks"
              )}
              {renderColumn(
                "Today",
                "#f59e0b",
                filteredAndSortedTasks.filter((t) => getDueDateCategory(t) === "Today"),
                "No tasks scheduled for today"
              )}
              {renderColumn(
                "Upcoming",
                "var(--primary)",
                filteredAndSortedTasks.filter((t) => getDueDateCategory(t) === "Upcoming"),
                "No upcoming tasks"
              )}
              {renderColumn(
                "Completed",
                "var(--status-completed-dot)",
                filteredAndSortedTasks.filter((t) => getDueDateCategory(t) === "Completed"),
                "No completed tasks yet"
              )}
            </div>
          )}
        </main>

      {/* Task Details Modal (Priority and Edit Task removed) */}
      {selectedTaskForModal && (
        <div
          className="canvas-modal-backdrop modal-backdrop show"
          id="detailsModal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="detailsTitle"
          onClick={() => setSelectedTaskForModal(null)}
        >
          <div className="canvas-modal-card modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="canvas-modal-header modal-header">
              <h3 id="detailsTitle">Task Details</h3>
              <button
                type="button"
                className="canvas-modal-close-btn modal-close-btn"
                id="closeDetailsBtn"
                aria-label="Close modal"
                onClick={() => setSelectedTaskForModal(null)}
              >
                ✕
              </button>
            </div>
            <div className="canvas-modal-body modal-body">
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value" id="detailsStatus">
                  {getEffectiveStatus(selectedTaskForModal)}
                </span>
              </div>
              {/* Priority detail row removed per specifications */}
              <div className="detail-row">
                <span className="detail-label">Due Date:</span>
                <span className="detail-value" id="detailsDue">
                  {formatLongDate(selectedTaskForModal.due_date)}
                </span>
              </div>
              {getTaskClient(selectedTaskForModal) && (
                <div className="detail-row" id="detailsClientRow">
                  <span className="detail-label">Client:</span>
                  <span className="detail-value" id="detailsClient">
                    {getTaskClient(selectedTaskForModal)}
                  </span>
                </div>
              )}
              <div>
                <span className="detail-label">Description:</span>
                <div className="detail-desc-box" id="detailsDescription">
                  {selectedTaskForModal.description || "No description provided."}
                </div>
              </div>
            </div>
            <div className="canvas-modal-footer modal-footer">
              <button
                type="button"
                className="btn btn-canvas-secondary"
                id="detailsToggleCompleteBtn"
                onClick={() => {
                  const target = selectedTaskForModal;
                  handleToggleComplete(target);
                  setSelectedTaskForModal((prev) =>
                    prev && prev.id === target.id
                      ? {
                          ...prev,
                          status: isTaskCompleted(prev) ? "Pending" : "Complete",
                        }
                      : null
                  );
                }}
              >
                {isTaskCompleted(selectedTaskForModal)
                  ? "Mark as Incomplete"
                  : "Mark as Complete"}
              </button>
              {/* Edit Task button removed per specifications */}
              <button
                type="button"
                className="btn btn-modal-action-primary"
                id="closeDetailsActionBtn"
                onClick={() => setSelectedTaskForModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
